import Hashids from "hashids"
import { format, parse, parseISO } from "date-fns"
import { BaseVariant, closeSnackbar, enqueueSnackbar, SnackbarMessage } from "notistack"
import {
  CurrencyMap,
  dmEnMessage1,
  dmEnMessage2,
  dmEnMessage3,
  dmScoutEnMessage1,
  dmScoutEnMessage2,
  dmScoutEnMessage3,
  dmScoutEnMessage4,
  dmScoutEnMessage5,
  dmScoutEnMessage6,
  dmScoutEnMessage7,
  dmTrMessage1,
  dmTrMessage2,
  dmTrMessage3,
  emailEnMessage1,
  emailEnMessage2,
  emailEnMessage3,
  emailEnSubject1,
  emailEnSubject2,
  emailEnSubject3,
  emailScoutEnMessage1,
  emailScoutEnMessage2,
  emailScoutEnMessage3,
  emailScoutEnMessage4,
  emailScoutEnMessage5,
  emailScoutEnMessage6,
  emailScoutEnMessage7,
  emailScoutEnMessage8,
  emailScoutEnMessage9,
  emailTrMessage1,
  emailTrMessage2,
  emailTrMessage3,
  emailTrSubject1,
  emailTrSubject2,
  emailTrSubject3,
  MessageContentTypeOptions,
  TrendOptions
} from "./constants"
import { KSelectOption } from "kahuna-base-react-components/dist/components/KDropdown/KDropdown"
import { ConstantModel, TaskStatus } from "types/constants"
import { User } from "types/users"
import { UserProfile, UserRoles } from "types/auth"
import { MessageContentType } from "types/system"

export const getValueText = (value: number | string) => {
  if (typeof value === "string") {
    return value
  }
  return value > 999999 ? `${(value / 1000000).toFixed(1)}M` : value > 999 ? `${(value / 1000).toFixed(1)}K` : value
}

export const getFilterText = (filter: any) => {
  if (!filter) return ""
  const stringifiedFilters = Object.keys(filter)
    .filter(
      (key) =>
        filter[key] !== undefined && filter[key] !== "" && !(Array.isArray(filter[key]) && filter[key].length === 0)
    )
    .map((key, idx) => `${key}=${filter[key]}${Object.keys(filter).length - 1 === idx ? "" : "&"}`)
  let endString = ""
  for (const filterString of stringifiedFilters) {
    endString = endString.concat(filterString)
  }

  if (!endString) {
    return ""
  }
  return `&${endString}`
}

export const getDate = (date: string) => {
  try {
    return format(parse(date, "yyyy-LL-dd", new Date()), "dd/LL/yyyy")
  } catch (e) {
    try {
      return format(parseISO(date), "dd/LL/yyyy")
    } catch (e) {
      return date
    }
  }
}

export const formatNumberComma = (text: string) => {
  if (!text) {
    return ""
  }
  const numericValue = text.replace(/\D/g, "")
  return numericValue.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",")
}

export const toastMessage = (message: string | SnackbarMessage, variant: BaseVariant, autoHideDuration?: number) => {
  const _key: any = enqueueSnackbar(message, {
    autoHideDuration: autoHideDuration || 2000,
    anchorOrigin: { vertical: "top", horizontal: "right" },
    variant,
    SnackbarProps: {
      onClick: () => closeSnackbar(_key), // close when snackbar is clicked
      style: { cursor: "pointer" } // pointer cursor
    }
  })
}

export const hashids = new Hashids("What comes first, goes last", 10)

export const turkishToEnglish = (name: string) => {
  // Normalize the string
  const normalizedName = name
    .normalize("NFKD")
    .replace(/[\u0300-\u036f]/g, "") // Remove diacritical marks
    .toLowerCase()

  // Replace spaces with hyphens
  return normalizedName.replace(/\s+/g, "-")
}

export const generateRandomColors = (count: number): string[] => {
  const colors: string[] = []
  for (let i = 0; i < count; i++) {
    const color = `rgba(${Math.floor(Math.random() * 255)}, ${Math.floor(Math.random() * 255)}, ${Math.floor(
      Math.random() * 255
    )}, 0.6)`
    colors.push(color)
  }
  return colors
}

export const getCurrency = (value?: string) => {
  const currencyString = value || "N/A"
  //@ts-ignore
  return Object.keys(CurrencyMap).includes(currencyString) ? CurrencyMap[currencyString] : "?"
}

export const getTrendIcon = (value?: number) => {
  return TrendOptions.find((trendOptions) => trendOptions.value === value)?.icon || ""
}

const convertToCSV = (array: string[][]) => {
  return array.map((row) => row.join(",")).join("\n")
}

export const downloadCSV = (data: string[][], fileName: string) => {
  const csvData = convertToCSV(data)
  const blob = new Blob([csvData], { type: "text/csv" })
  const url = URL.createObjectURL(blob)
  const link = document.createElement("a")
  link.href = url
  link.setAttribute("download", fileName)
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
}

export const findConstantDefaultValue = (options: ConstantModel[], value: string) => {
  const option = options.find((option) => option.id.toString() === value)
  if (option) {
    return { label: option.name, value: option.id }
  }
  return undefined
}

export const findSelectDefaultLabel = (options: KSelectOption[], label?: string) => {
  if (!label) return undefined

  const option = options.find((option) => option.label.toString() === label)
  if (option) {
    return { label: option.label, value: option.value }
  }
  return undefined
}

export const findSelectValue = (options: KSelectOption[], value?: number) => {
  if (!value) return undefined

  const option = options.find((option) => option.value === value)
  if (option) {
    return { label: option.label, value: option.value }
  }
  return undefined
}

export const findSelectValue2 = (options: KSelectOption[], value2?: string) => {
  if (!value2) return undefined
  const option = options.find((option) => option.value2 === value2)
  if (option) {
    return { label: option.label, value: option.value, value2 }
  }
  return undefined
}

export const findConstantDefaultValues = (options: ConstantModel[], valueList: string[]) => {
  return options
    .filter((option) => valueList.includes(option.id.toString()))
    .map((option) => {
      return { label: option.name, value: option.id }
    })
}

export const findSelectDefaultLabels = (options: KSelectOption[], labelList?: string[]) => {
  if (!labelList) return []
  return options
    .filter((option) => labelList.includes(option.label))
    .map((option) => {
      return { label: option.label, value: option.value }
    })
}

export const findSelectDefaultValues = (options: KSelectOption[], valueList: number[]) => {
  return options
    .filter((option) => valueList.includes(option.value))
    .map((option) => {
      return { label: option.label, value: option.value }
    })
}

export const findSelectDefaultValues2 = (options: KSelectOption[], value2List: number[]) => {
  return options
    .filter((option) => value2List.join(",").includes(option.value2 || ""))
    .map((option) => {
      return { label: option.label, value: option.value, value2: option.value2 }
    })
}

export const getDMTrMessage = (
  messageContents: MessageContentType[],
  artistName: string,
  trackTitle: string,
  userName: string,
  distributorName: string,
  dmCount: number
) => {
  const messageContent = [...messageContents].find(
    (mc) => mc?.number === Number(dmCount) && mc.lang.toLowerCase() === "tr" && mc.type === MessageContentTypeOptions.DM
  )
  let trMessage = messageContent?.value || ""
  trMessage = trMessage
    .replaceAll("<artist_name>", artistName)
    .replaceAll("<user_name>", userName)
    .replaceAll("<distributor_name>", distributorName)
  if (trackTitle) trMessage = trMessage.replaceAll("<track_title>", trackTitle)
  return trMessage
}

export const getDMEnMessage = (artistName: string, trackTitle: string, dmCount: number) => {
  let enMessage = dmEnMessage1
  if (dmCount === 1) enMessage = dmEnMessage2
  else if (dmCount >= 2) enMessage = dmEnMessage3
  enMessage = enMessage.replaceAll("<artist_name>", artistName).replaceAll("<track_title>", trackTitle)

  return enMessage
}

export const getScoutDMEnMessage = (
  messageContents: MessageContentType[],
  artistName: string,
  trackTitle: string,
  userName: string,
  distributorName: string,
  dmCount: number
) => {
  const messageContent = [...messageContents].find(
    (mc) => mc?.number === Number(dmCount) && mc.lang.toLowerCase() === "en" && mc.type === MessageContentTypeOptions.DM
  )
  let enMessage = messageContent?.value || ""
  enMessage = enMessage
    .replaceAll("<artist_name>", artistName)
    .replaceAll("<user_name>", userName)
    .replaceAll("<distributor_name>", distributorName)

  if (trackTitle) enMessage = enMessage.replaceAll("<track_title>", trackTitle)

  return enMessage
}

export const getEmailTrMessageAndSubject = (
  messageContents: MessageContentType[],
  artistName: string,
  trackTitle: string,
  userFirstName: string,
  distributorName: string,
  emailCount: number
) => {
  const messageContent = [...messageContents].find(
    (mc) =>
      mc?.number === Number(emailCount) && mc.lang.toLowerCase() === "tr" && mc.type === MessageContentTypeOptions.EMAIL
  )
  let trMessage = messageContent?.value || ""
  trMessage = trMessage
    .replaceAll("<artist_name>", artistName)
    .replaceAll("<user_name>", userFirstName)
    .replaceAll("<distributor_name>", distributorName)

  if (trackTitle) trMessage = trMessage.replaceAll("<track_title>", trackTitle)

  let trSubject = messageContent?.subject || ""
  trSubject = trSubject
    .replaceAll("<artist_name>", artistName)
    .replaceAll("<user_name>", userFirstName)
    .replaceAll("<distributor_name>", distributorName)
  if (trackTitle) trSubject = trSubject.replaceAll("<track_title>", trackTitle)
  return [trMessage, trSubject]
}

export const getEmailEnMessageAndSubject = (
  artistName: string,
  trackTitle: string,
  userFirstName: string,
  emailCount: number
) => {
  let enMessage = emailEnMessage1
  if (emailCount === 1) enMessage = emailEnMessage2
  else if (emailCount >= 2) enMessage = emailEnMessage3
  enMessage = enMessage
    .replaceAll("<artist_name>", artistName)
    .replaceAll("<track_title>", trackTitle)
    .replaceAll("<user>", userFirstName)

  let enSubject = emailEnSubject1
  if (emailCount === 1) enSubject = emailEnSubject2
  else if (emailCount >= 2) enSubject = emailEnSubject3
  enSubject = enSubject
    .replaceAll("<artist_name>", artistName)
    .replaceAll("<track_title>", trackTitle)
    .replaceAll("<user>", userFirstName)

  return [enMessage, enSubject]
}

export const getScoutEmailEnMessageAndSubject = (
  messageContents: MessageContentType[],
  artistName: string,
  trackTitle: string,
  userFirstName: string,
  distributorName: string,
  emailCount: number
) => {
  const messageContent = [...messageContents].find(
    (mc) =>
      mc?.number === Number(emailCount) && mc.lang.toLowerCase() === "en" && mc.type === MessageContentTypeOptions.EMAIL
  )
  let enMessage = messageContent?.value || ""
  enMessage = enMessage
    .replaceAll("<artist_name>", artistName)
    .replaceAll("<user_name>", userFirstName)
    .replaceAll("<distributor_name>", distributorName)
  if (trackTitle) enMessage = enMessage.replaceAll("<track_title>", trackTitle)

  let enSubject = messageContent?.subject || ""
  enSubject = enSubject
    .replaceAll("<artist_name>", artistName)
    .replaceAll("<user_name>", userFirstName)
    .replaceAll("<distributor_name>", distributorName)
  if (trackTitle) enSubject = enSubject.replaceAll("<track_title>", trackTitle)

  return [enMessage, enSubject]
}

export const dateToString = (date?: Date) => {
  if (!date) return undefined

  const yyyy = date.getFullYear()
  const mm = String(date.getMonth() + 1).padStart(2, "0") // months are zero-based
  const dd = String(date.getDate()).padStart(2, "0")
  return `${yyyy}-${mm}-${dd}`
}

export const checkAdminRoleExist = (user?: UserProfile) => {
  return user?.role_ids?.includes(UserRoles.SCOUTING_ADMIN) || false
}

export const sortTaskStatusOptions = (taskStatuses: TaskStatus[]) => {
  const sortedTaskStatusList = taskStatuses.sort((a, b) => {
    const aPriority = a.priority ?? 0
    const bPriority = b.priority ?? 0

    if (aPriority !== bPriority) {
      return aPriority - bPriority // sort by priority first
    }

    const aPosition = a.position ?? 0
    const bPosition = b.position ?? 0

    return aPosition - bPosition // if priority equal, sort by position
  })

  return sortedTaskStatusList.map((taskStatus: TaskStatus) => {
    return {
      value: taskStatus.id || 0,
      label: taskStatus.name || "",
      label2: taskStatus.color,
      value2: taskStatus.priority?.toString()
    }
  })
}

export const normalizeLabel = (pLine: string) => {
  return pLine
    .replaceAll("℗", "")
    .replaceAll("(P)", "")
    .replaceAll("(C)", "")
    .replaceAll("(p)", "")
    .replaceAll(/\b\d{4}\b/g, "")
    .replaceAll(/\s+/g, " ")
    .trim()
}

export const addDays = (days: number) => {
  const result = new Date()
  result.setDate(result.getDate() + days)
  return result.toISOString().split("T")[0] // YYYY-MM-DD
}
